import { axiosServiceNowApi } from 'services/Api'
import { ROUTES_NAMES } from 'constants/servicenow.routes'
import {
    IAccountabilityMatrix,
    ICreateNotification,
    INotification,
    INotificationPlan,
    INotificationArchiveBody,
    NotificationType,
    ICreateNotificationPlanPayLoad,
    ICreateNewNotification,
    ICreateNewNotificationResponse,
    IBatchActionPlanRequest,
    IBatchActionPlanResponse,
    IIshikawaPayload,
    IBatchIshikawaRequest,
    IBatchIshikawaResponse
} from 'types/notification'
import { notificationStatus } from 'constants/notificationStatus'
import { NotificationSubStatus } from 'constants/notificationSubState'
import moment from 'moment'
import axios from 'axios'
import { applyAuthorizationHeader } from 'helper/getAuthHeader'

interface ITreatmentGroup {
    responsible: string          // sys_id do usuário responsável
    u_notifica_user: string     // sys_id do usuário que assumiu o tratamento
    u_location: string          // sys_id da unidade/localização
    u_sector: string           // sys_id do setor
    u_group_damage_rating: string  // sys_id do grau de dano
    u_incident_type: string    // sys_id do tipo de incidente
    notification_ids: string[]  // array com sys_ids das notificações do grupo
    u_group_key?: string
}

/**
 * GET ishikawa_by_notificationid e getIshikawaByNotificationId gravam `res.data` no Redux.
 * A API pode devolver o diagrama plano ou `{ result: [ {...} ] }`. Sem normalizar, sys_id some
 * e o mapa/batch de update em grupo falha.
 */
export function normalizeIshikawaApiResponseBody(data: unknown): Record<string, any> | null {
    if (data == null || typeof data !== 'object' || Array.isArray(data)) {
        return null
    }
    const d = data as Record<string, any>
    if (Array.isArray(d.result)) {
        const first = d.result[0]
        if (first && typeof first === 'object' && !Array.isArray(first)) {
            return first as Record<string, any>
        }
        return null
    }
    if (d.result != null && typeof d.result === 'object' && !Array.isArray(d.result)) {
        return d.result as Record<string, any>
    }
    return d
}

const NotificationService = {
    createNotification: async (
        notification: ICreateNewNotification
    ): Promise<ICreateNewNotificationResponse> => {
        const response: any = await axiosServiceNowApi?.post(
            ROUTES_NAMES.notification.post.create,
            notification
        )
        return response?.data?.result
    },
    createNotificationByPatient: async (
        notification: ICreateNewNotification
    ): Promise<ICreateNewNotificationResponse> => {
        const response: any = await axiosServiceNowApi?.post(
            ROUTES_NAMES.notification.post.create_by_patient,
            notification
        )
        return response?.data?.result
    },
    updateNotification: async (
        notification: ICreateNotification,
        sys_id: string
    ): Promise<any> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            notification
        )
    },
    updateNotificationGeneric: async (
        notification: any,
        sys_id: string
    ): Promise<any> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            notification
        )
    },
    attachNotification: async (data: FormData): Promise<any> => {
        return axiosServiceNowApi?.post(
            ROUTES_NAMES.notification.post.attach,
            data,
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        )
    },
    readerNotification: async (
        sys_id: string,
        notification: INotification
    ): Promise<INotification> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            { u_notification_read: 'true' }
        )
    },
    saveIshikawaDiagram: async (
        ishikawaDiagram: IIshikawaPayload
    ): Promise<INotification | IBatchIshikawaResponse> => {
        if (!ishikawaDiagram.sys_id) {
            const batchRequest = {
                records: [ishikawaDiagram],
                options: {
                    stopOnError: false
                }
            }
            const response = await axiosServiceNowApi?.post(
                ROUTES_NAMES.notification.post.create_ishikawa,
                batchRequest
            )
            return response?.data?.result
        } else {
            const batchRequest = {
                records: [ishikawaDiagram],
                options: {
                    stopOnError: false
                }
            }
            const response = await axiosServiceNowApi?.put(
                ROUTES_NAMES.notification.put.update_ishikawa,
                batchRequest
            )
            return response?.data?.result
        }
    },
    saveLondonProtocol: async (
        londonProtocol: any,
        notification: NotificationType
    ): Promise<INotification | string> => {
        try {
            const updateNotification = {
                u_never_events_reference:
                    notification?.u_never_events_reference?.value
            }

            NotificationService.updateNotificationGeneric(
                updateNotification,
                notification?.sys_id?.value
            )
            console.log(
                'londonProtocol.sys_id',
                londonProtocol.sys_id,
                !londonProtocol.sys_id
            )
            return !londonProtocol.sys_id
                ? axiosServiceNowApi?.post(
                      ROUTES_NAMES.notification.post.create_london_protocol,
                      londonProtocol
                  )
                : axiosServiceNowApi?.put(
                      ROUTES_NAMES.notification.put.update_london_protocol(
                          londonProtocol.sys_id
                      ),
                      londonProtocol
                  )
        } catch (error) {
            //TODO: tratamento desse erro?
        }
    },
    saveAccountabilityMatrix: async (
        matrix: IAccountabilityMatrix
    ): Promise<INotification> => {
        try {
            return !matrix.sys_id
                ? axiosServiceNowApi?.post(
                      ROUTES_NAMES.notification.post
                          .create_accountability_matrix,
                      matrix
                  )
                : axiosServiceNowApi?.put(
                      ROUTES_NAMES.notification.put.update_accountability_matrix(
                          matrix.sys_id
                      ),
                      matrix
                  )
        } catch (error) {
            //TODO: tratamento desse erro?
        }
    },
    archiveNotification: async (
        sys_id: string,
        notificationArchive: INotificationArchiveBody
    ): Promise<INotification> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            notificationArchive
        )
    },
    finishNotification: async (
        sys_id: string,
        type: 'finish' | 'forward',
        user_id: string,
        disclosure: any
    ): Promise<INotification> => {
        const currentDateTime = moment()
        const formattedDateTime = currentDateTime.format('YYYY-MM-DD HH:mm:ss')

        const stat =
            type === 'finish'
                ? notificationStatus[4]?.value
                : notificationStatus[3]?.value
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            {
                u_state: stat,
                ...disclosure,
                ...(type === 'forward'
                    ? {
                          u_treatment_by: user_id,
                          u_date_and_time_closed_by_manager: formattedDateTime
                      }
                    : {}),
                ...(type === 'finish'
                    ? {
                          u_assigned_to: user_id,
                          u_date_and_hour_notification_closed: formattedDateTime,
                          u_sub_state: 'u_finalizada_com_tratativa'
                      }
                    : {})
            }
        )
    },
    encaminharNotification: async (
        sys_id: string,
        sysUserName: string
    ): Promise<INotification> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            { u_state: notificationStatus[1].value, forwarded_by: sysUserName }
        )
    },
    customUpdate: async (sys_id: string, data: any): Promise<INotification> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            data
        )
    },
    assumirNotification: async (
        sys_id: string,
        assignedTo: string
    ): Promise<INotification> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update(sys_id),
            {
                u_state: notificationStatus[2].value,
                u_assigned_to: assignedTo
            }
        )
    },

    assumirGroupTreatment: async (
        notificationIds: string[],
        assignedTo: string
    ): Promise<any> => {
        const payload = {
            records: notificationIds.map(id => ({
                sys_id: id,
                u_assigned_to: assignedTo
            })),
            options: {
                stopOnError: false
            }
        }
        
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.update_bulk,
            payload
        )
    },
    reopenNotification: async (
        sys_id: string,
        reopenedBy: string,
        currentStatus?: string,
        subState?: string
    ): Promise<INotification> => {
        const isArchived = currentStatus === 'Arquivada'
        
        const record: any = {
            sys_id: sys_id,
            u_notifica_user: reopenedBy
        }
        
        // Se estiver arquivada, voltar para status "Nova" e limpar campos de arquivamento
        if (isArchived) {
            record.u_state = notificationStatus[0].value // '1' = Nova
            record.u_reason_for_archiving = '' // Limpar justificativa
            record.u_archiving_observations = '' // Limpar observações
        } else {
            // Se estiver finalizada, verificar o substatus
            if (subState === NotificationSubStatus.FINALIZADA_SEM_TRATATIVA) {
                // Sem tratativa → voltar para "Nova"
                record.u_state = notificationStatus[0].value // '1' = Nova
            } else {
                // Com tratativa → voltar para "Em Tratamento"
                record.u_state = notificationStatus[2].value // '3' = Em Tratamento
            }
        }
        
        // Zerar o campo de sub status ao reabrir
        record.u_sub_state = ''
        
        const payload = {
            records: [record]
        }
        
        return axiosServiceNowApi?.put(
            '/resource/notification/reopen_bulk',
            payload
        )
    },

    reopenGroupNotification: async (
        notificationIds: string[],
        reopenedBy: string
    ): Promise<INotification> => {
        // Grupos sempre voltam para "Em Tratamento" pois não há finalizadas sem tratativa em grupos
        const records = notificationIds.map(id => ({
            sys_id: id,
            u_notifica_user: reopenedBy,
            u_state: notificationStatus[2].value, // '3' = Em Tratamento
            u_sub_state: '' // Zerar o campo de sub status ao reabrir
        }))
        
        const payload = {
            records,
            options: {
                stopOnError: false
            }
        }
        
        return axiosServiceNowApi?.put(
            '/resource/notification/reopen_bulk',
            payload
        )
    },
    

    getDamageLevel: (): Promise<INotification> => {
        return axiosServiceNowApi?.get(
            ROUTES_NAMES.notification.get.damage_level
        )
    },

    createNotificationPlan: async (
        plan: ICreateNotificationPlanPayLoad
    ): Promise<INotification> => {
        return axiosServiceNowApi?.post(
            ROUTES_NAMES.notification.post.createPlan,
            plan
        )
    },
    //notificationPlan
    updateNotificationPlan: async (
        plan: ICreateNotificationPlanPayLoad,
        planId: string
    ): Promise<INotification> => {
        return axiosServiceNowApi?.put(
            ROUTES_NAMES.notification.put.updatePlanAction(planId),
            plan
        )
    },
    deleteNotificationPlan: async (planId: string): Promise<INotification> => {
        return axiosServiceNowApi?.delete(
            ROUTES_NAMES.notification.delete.deletePlanAction(planId)
        )
    },
    getNotificationById: async (id: string): Promise<INotification> => {
        return axiosServiceNowApi?.get(
            ROUTES_NAMES.notification.get.byNotificationid(id)
        )
    },
    uploadFileS3: async (
        file: File
    ): Promise<{ url: string; presignedUrl: string }> => {
        if (!file) {
            console.log('Arquivo não enviado. Tente novamente.')
            throw new Error(
                'Ocorreu um erro ao realizar upload do arquivo. Tente novamente.'
            )
        }

        const form = new FormData()
        form.append('file', file)

        const url = `${process.env.REACT_APP_PROXY_URL}${ROUTES_NAMES.notification.put.create_s3_file}`
        const headers: Record<string, string> = {
            'Content-type': 'multipart/form-data'
        }
        applyAuthorizationHeader(headers)

        let data
        await axios
            .put(url, form, { headers: headers })
            .then((res) => {
                data = res.data
            })
            .catch((e) => {
                console.log(
                    'Ocorreu um erro ao realizar upload do arquivo: ',
                    e
                )
                throw new Error(
                    'Ocorreu um erro ao realizar upload do arquivo. Tente novamente.'
                )
            })

        return data
    },
    createPresignedURL: async (
        currentFile: File,
        setProgressValue?: React.Dispatch<React.SetStateAction<number>>
    ): Promise<{ url: string }> => {
        if (!currentFile) {
            console.log('Arquivo não enviado. Tente novamente.')
            throw new Error(
                'Ocorreu um erro ao realizar upload do arquivo. Tente novamente.'
            )
        }

        try {
            //criar url pre-assinada vazia
            const fileExtension = currentFile?.name.split('.').pop()
            const url = `${process.env.REACT_APP_PROXY_URL}/resource/notification/create_presigned_url|${fileExtension}`
            const headers: Record<string, string> = {
                'Content-type': 'multipart/form-data'
            }
            applyAuthorizationHeader(headers)
            const response = await axios.post(url, { ext: `` }, { headers })

            if (response?.status !== 200 || !response?.data?.url) {
                console.error('Erro ao obter URL pré-assinada:', response)
                throw new Error(
                    'Erro ao obter URL pré-assinada. Tente novamente.'
                )
            }

            //realiza upload do arquivo na url pre-assinada vazia
            const objectS3Url = response.data.url
            await axios.put(objectS3Url?.signedUrl, currentFile, {
                headers: {
                    'Content-Type': currentFile.type
                },
                onUploadProgress: (e) => {
                    var percent = (e.loaded / e.total) * 100
                    console.log(
                        'On upload progress percent: ',
                        Math.round(percent) + '%'
                    )
                    setProgressValue(Math.round(percent))
                }
            })

            return { url: objectS3Url?.key }
        } catch (error) {
            console.error('Erro no processo de upload:', error)
            throw new Error(
                'Ocorreu um erro durante o upload do arquivo. Tente novamente.'
            )
        }
    },
    getPresignedUrlById: async (id: string): Promise<string> => {
        return await axiosServiceNowApi?.get(
            ROUTES_NAMES.notification.get.presigned_url_by_id(id)
        )
    },
    deleteS3AttachedFile: async (
        u_attachments_url: string
    ): Promise<string> => {
        const form = new FormData()
        form.append('u_attachments_url', u_attachments_url)

        const url = `${process.env.REACT_APP_PROXY_URL}${ROUTES_NAMES.notification.put.delete_s3_file}`
        const headers: Record<string, string> = {
            'Content-type': 'multipart/form-data'
        }
        applyAuthorizationHeader(headers)

        let data: any
        await axios
            .put(url, form, { headers: headers })
            .then((res) => {
                data = res.data
            })
            .catch((e) => {
                console.log(
                    'Ocorreu um erro ao realizar upload do arquivo: ',
                    e
                )
            })

        return data
    },
    getAttachmentBucketS3: async (
        url: string,
        setProgressBarValue?: React.Dispatch<React.SetStateAction<number>>
    ): Promise<any> => {
        const response = await axios.get(url, {
            responseType: 'blob',
            onDownloadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent
                if (total) {
                    const percentCompleted = Math.round((loaded / total) * 100)
                    console.log(`Download Progress: ${percentCompleted}%`)
                    setProgressBarValue(percentCompleted)
                }
            }
        })

        return response.data
    },
    
    // Método para criar grupo de tratamento
    createTreatmentGroup: async (
        groupData: ITreatmentGroup
    ): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.post(
                ROUTES_NAMES.treatment_groups.post.create,
                groupData
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao criar grupo de tratamento:', error)
            throw error
        }
    },
    
    
    // Método para buscar notificações por ID de grupo
    getNotificationsByGroupId: async (
        groupId: string
    ): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.get(
                ROUTES_NAMES.notification.get.byGroupId(groupId)
            )
            return response?.data?.result
        } catch (error) {
            console.error('Erro ao buscar notificações do grupo:', error)
            throw error
        }
    },

    // Método para buscar dados completos do grupo por ID
    getGroupDataById: async (
        groupId: string
    ): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.get(
                `/resource/notification/by_group_key|${groupId}`
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao buscar dados do grupo:', error)
            throw error
        }
    },

    // Método para remover notificações do grupo de tratamento
    removeFromTreatmentGroup: async (
        notificationIds: string[],
        targetStatus: string
    ): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.delete(
                ROUTES_NAMES.notification.delete.remove_from_treatment_group(),
                {
                    data: {
                        notification_ids: notificationIds,
                        target_status: targetStatus
                    }
                }
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao remover notificações do grupo:', error)
            throw error
        }
    },

    // Método para buscar múltiplas notificações por IDs
    getNotificationsByIds: async (
        ids: string[]
    ): Promise<any> => {
        try {
            const idsString = Array.isArray(ids) ? ids.join(',') : ids;
            
            const response = await axiosServiceNowApi?.get(
                ROUTES_NAMES.notification.get.byIds(idsString)
            )
            
            return response?.data?.result || []
        } catch (error: any) {
            console.error('Erro ao buscar notificações por IDs:', error)
            
            // Tratamento específico de erros
            if (error?.response?.status === 403) {
                throw new Error('Sem permissão para acessar essas notificações')
            }
            if (error?.response?.status === 400) {
                throw new Error('Lista de IDs é obrigatória')
            }
            
            throw error
        }
    },

    // Métodos específicos para Plano de Ação em Grupo
    createNotificationPlanGroup: async (
        groupId: string,
        plan: ICreateNotificationPlanPayLoad
    ): Promise<INotification> => {
        try {
            const response = await axiosServiceNowApi?.post(
                `/resource/notification/group/${groupId}/action_plan`,
                plan
            )
            return response?.data?.result
        } catch (error) {
            console.error('Erro ao criar plano de ação do grupo:', error)
            throw error
        }
    },

    updateNotificationPlanGroup: async (
        groupId: string,
        plan: ICreateNotificationPlanPayLoad,
        planId: string
    ): Promise<INotification> => {
        try {
            const response = await axiosServiceNowApi?.put(
                `/resource/notification/group/${groupId}/action_plan/${planId}`,
                plan
            )
            return response?.data?.result
        } catch (error) {
            console.error('Erro ao atualizar plano de ação do grupo:', error)
            throw error
        }
    },

    deleteNotificationPlanGroup: async (
        groupId: string,
        planId: string
    ): Promise<INotification> => {
        try {
            const response = await axiosServiceNowApi?.delete(
                `/resource/notification/group/${groupId}/action_plan/${planId}`
            )
            return response?.data?.result
        } catch (error) {
            console.error('Erro ao deletar plano de ação do grupo:', error)
            throw error
        }
    },

    getListOfActionPlanGroup: async (
        groupId: string
    ): Promise<INotification> => {
        try {
            const response = await axiosServiceNowApi?.get(
                `/resource/notification/group/${groupId}/action_plans`
            )
            return response?.data?.result
        } catch (error) {
            console.error('Erro ao buscar planos de ação do grupo:', error)
            throw error
        }
    },

    /** Mesmo GET usado em getListOfActionPlan (Redux); necessário para montar batch como no Ishikawa */
    fetchActionPlansByNotificationId: async (
        notificationId: string
    ): Promise<INotificationPlan[]> => {
        try {
            const response = await axiosServiceNowApi?.get(
                ROUTES_NAMES.notification.get.planAction(notificationId)
            )
            const result = response?.data?.result
            return Array.isArray(result) ? result : []
        } catch (error) {
            console.error('Erro ao buscar planos de ação da notificação:', error)
            throw error
        }
    },

    // Método para criar plano de ação em massa (batch)
    createActionPlanBatch: async (
        request: IBatchActionPlanRequest
    ): Promise<IBatchActionPlanResponse> => {
        try {
            const response = await axiosServiceNowApi?.post(
                ROUTES_NAMES.notification.post.create_action_plan,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao criar planos de ação em massa:', error)
            throw error
        }
    },

    updateActionPlanBatch: async (
        request: IBatchActionPlanRequest
    ): Promise<IBatchActionPlanResponse> => {
        try {
            const response = await axiosServiceNowApi?.put(
                ROUTES_NAMES.notification.put.update_action_plan_batch,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao atualizar planos de ação em massa:', error)
            throw error
        }
    },

    deleteActionPlanBatch: async (
        request: IBatchActionPlanRequest
    ): Promise<IBatchActionPlanResponse> => {
        try {
            const response = await axiosServiceNowApi?.delete(
                ROUTES_NAMES.notification.delete.delete_action_plan_batch,
                { data: request }
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao deletar planos de ação em massa:', error)
            throw error
        }
    },

    // Método para finalizar tratamento em grupo (atualização em lote)
    finishGroupTreatment: async (
        notifications: Array<{ sys_id: string; value: string }>,
        userId: string,
        disclosure?: any
    ): Promise<any> => {
        try {
            const currentDateTime = moment()
            const formattedDateTime = currentDateTime.format('YYYY-MM-DD HH:mm:ss')

            const records = notifications.map(notification => ({
                sys_id: notification.sys_id,
                u_state: notificationStatus[4]?.value, // Status "Finalizada"
                u_assigned_to: userId,
                u_date_and_hour_notification_closed: formattedDateTime,
                u_sub_state: 'u_finalizada_com_tratativa', // Setar sub status ao finalizar
                ...disclosure
            }))

            const response = await axiosServiceNowApi?.put(
                ROUTES_NAMES.notification.put.update_bulk,
                {
                    records,
                    options: {
                        stopOnError: false // Continua mesmo se houver erros
                    }
                }
            )

            return response?.data
        } catch (error) {
            console.error('Erro ao finalizar tratamento em grupo:', error)
            throw error
        }
    },

    // Métodos para Ishikawa em massa (batch)
    createIshikawaBatch: async (
        request: any
    ): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.post(
                ROUTES_NAMES.notification.post.create_ishikawa,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao criar diagramas de Ishikawa em massa:', error)
            throw error
        }
    },

    updateIshikawaBatch: async (
        request: any
    ): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.put(
                ROUTES_NAMES.notification.put.update_ishikawa,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao atualizar diagramas de Ishikawa em massa:', error)
            throw error
        }
    },

    createLondonProtocolBatch: async (request: any): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.post(
                ROUTES_NAMES.notification.post.create_london_protocol_batch,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao criar Protocolo de Londres em massa:', error)
            throw error
        }
    },

    updateLondonProtocolBatch: async (request: any): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.put(
                ROUTES_NAMES.notification.put.update_london_protocol_batch,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao atualizar Protocolo de Londres em massa:', error)
            throw error
        }
    },

    /** Uma linha de protocolo por notificação (sem alterar o Redux) — batch em grupo. */
    getLondonProtocolRowByNotificationId: async (
        notificationId: string
    ): Promise<Record<string, any> | null> => {
        try {
            const response = await axiosServiceNowApi?.get(
                ROUTES_NAMES.notification.get.london_protocol_by_notificationid(notificationId)
            )
            const row = response?.data?.result?.[0]
            return row ?? null
        } catch (error) {
            console.error('Erro ao buscar Protocolo de Londres por notificacao:', error)
            return null
        }
    },

    /** Uma linha de Ishikawa por notificação (sem alterar o Redux) — batch em grupo. */
    getIshikawaRowByNotificationId: async (
        notificationId: string
    ): Promise<Record<string, any> | null> => {
        try {
            const response = await axiosServiceNowApi?.get(
                ROUTES_NAMES.notification.get.ishikawa_by_notificationid(notificationId)
            )
            return normalizeIshikawaApiResponseBody(response?.data)
        } catch (error) {
            console.error('Erro ao buscar Ishikawa por notificacao:', error)
            return null
        }
    },

    createAccountabilityMatrixBatch: async (request: any): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.post(
                ROUTES_NAMES.notification.post.create_accountability_matrix_batch,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao criar Matriz de Responsabilização em massa:', error)
            throw error
        }
    },

    updateAccountabilityMatrixBatch: async (request: any): Promise<any> => {
        try {
            const response = await axiosServiceNowApi?.put(
                ROUTES_NAMES.notification.put.update_accountability_matrix_batch,
                request
            )
            return response?.data
        } catch (error) {
            console.error('Erro ao atualizar Matriz de Responsabilização em massa:', error)
            throw error
        }
    }
}

export default NotificationService
